import { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, Copy, Mail, RefreshCw, UserPlus, Users, UserX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSession } from '@/context/SessionContext';
import {
  createTenantMemberInvite,
  listTenantMemberInvites,
  listTenantUsers,
  revokeTenantMember,
  revokeTenantMemberInvite,
  type TenantMemberInvite,
  type TenantUserRecord,
} from '@/lib/tenant-users';
import { useTenantUsersAccess } from '@/lib/useTenantUsersAccess';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, toast } from '@workspace/ui';
import {
  Callout,
  cn,
  HorizontalStack,
  ManagementRegistry,
  RegistryAvatar,
  RegistryEmptyState,
  RegistryStatus,
  SilkInput,
  SilkSelect,
  VerticalStack,
  CondensedToolbar,
  ToolbarItem,
  DataTable,
  type ColumnDef,
} from '@workspace/silk-ui';

const ROLE_OPTIONS = [
  { value: 'tenant.viewer', label: 'Viewer' },
  { value: 'tenant.billing', label: 'Billing' },
  { value: 'tenant.admin', label: 'Admin' },
  { value: 'tenant.owner', label: 'Owner' },
];

type RoleVariant = 'default' | 'success' | 'warning' | 'error' | 'muted';

const ROLE_VARIANT_MAP: Record<string, RoleVariant> = {
  'tenant.owner': 'warning',
  'tenant.admin': 'default',
  'tenant.billing': 'success',
  'tenant.viewer': 'muted',
};

function getRoleVariant(roleCode: string): RoleVariant {
  return ROLE_VARIANT_MAP[roleCode] ?? 'muted';
}

function formatInviteLink(invite: TenantMemberInvite) {
  if (typeof window === 'undefined') {
    return `/join/member?member_invite=${encodeURIComponent(invite.inviteToken)}`;
  }
  return `${window.location.origin}/join/member?member_invite=${encodeURIComponent(invite.inviteToken)}`;
}

function formatUserLabel(user: TenantUserRecord) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email || user.username || user.id;
}

function formatRoleLabel(roleCode: string) {
  return ROLE_OPTIONS.find(o => o.value === roleCode)?.label ?? roleCode;
}

function formatRelativeTime(iso: string) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

type TenantUsersSection = 'active' | 'invites';

interface TenantUsersPageProps {
  section?: TenantUsersSection;
}

interface ConfirmState {
  type: 'member' | 'invite';
  id: string;
  label: string;
}

export default function TenantUsersPage({ section = 'active' }: TenantUsersPageProps) {
  const { session } = useSession();
  const { tenantId, canManage } = useTenantUsersAccess();
  
  const [search, setSearch] = useState('');
  
  const [users, setUsers] = useState<TenantUserRecord[]>([]);
  const [invites, setInvites] = useState<TenantMemberInvite[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [roleCode, setRoleCode] = useState('tenant.viewer');
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);

  const loadData = useCallback(async () => {
    if (!tenantId || !canManage) {
      setUsers([]);
      setInvites([]);
      return;
    }
    setLoading(true);
    try {
      const [nextUsers, nextInvites] = await Promise.all([
        listTenantUsers(tenantId),
        listTenantMemberInvites(tenantId),
      ]);
      setUsers(nextUsers);
      setInvites(nextInvites);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load tenant users.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [canManage, tenantId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const currentUserId = session?.userId ?? session?.id ?? '';

  const pendingInvites = useMemo(
    () => invites.filter(invite => invite.status === 'pending'),
    [invites],
  );

  const activeMembers = useMemo(
    () =>
      users.filter(user =>
        (user.tenantMemberships ?? []).some(
          m => m.tenantId === tenantId && m.status !== 'revoked',
        ),
      ),
    [tenantId, users],
  );

  const adminCount = useMemo(
    () =>
      activeMembers.filter(user => {
        const m = (user.tenantMemberships ?? []).find(m => m.tenantId === tenantId);
        return (m?.roles ?? []).some(r => r === 'tenant.admin' || r === 'tenant.owner');
      }).length,
    [activeMembers, tenantId],
  );

  const handleCreateInvite = useCallback(async () => {
    if (!tenantId || !email.trim()) {
      toast.error('Enter an email address first.');
      return;
    }
    setCreating(true);
    try {
      const invite = await createTenantMemberInvite(tenantId, { email: email.trim(), roleCode });
      setEmail('');
      setInviteOpen(false);
      await navigator.clipboard?.writeText?.(formatInviteLink(invite));
      toast.success('Invite created and link copied to clipboard.');
      await loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create teammate invite.';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  }, [email, loadData, roleCode, tenantId]);

  const handleCopyInvite = useCallback(async (invite: TenantMemberInvite) => {
    try {
      await navigator.clipboard?.writeText?.(formatInviteLink(invite));
      toast.success('Invite link copied.');
    } catch {
      toast.error('Failed to copy the invite link.');
    }
  }, []);

  const handleRevokeInvite = useCallback(
    async (inviteId: string) => {
      if (!tenantId) return;
      setActingId(inviteId);
      try {
        await revokeTenantMemberInvite(tenantId, inviteId);
        toast.success('Invite revoked.');
        await loadData();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to revoke invite.';
        toast.error(message);
      } finally {
        setActingId(null);
      }
    },
    [loadData, tenantId],
  );

  const handleRevokeMember = useCallback(
    async (userId: string) => {
      if (!tenantId) return;
      setActingId(userId);
      try {
        await revokeTenantMember(tenantId, userId);
        toast.success('Tenant access revoked.');
        await loadData();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to revoke tenant access.';
        toast.error(message);
      } finally {
        setActingId(null);
      }
    },
    [loadData, tenantId],
  );

  const handleConfirmRevoke = useCallback(async () => {
    if (!confirmState) return;
    const snap = confirmState;
    setConfirmState(null);
    if (snap.type === 'member') {
      await handleRevokeMember(snap.id);
    } else {
      await handleRevokeInvite(snap.id);
    }
  }, [confirmState, handleRevokeMember, handleRevokeInvite]);

  const activeColumns = useMemo<ColumnDef<TenantUserRecord>[]>(() => [
    {
      id: 'member',
      header: 'Member',
      cell: ({ row }) => {
        const user = row.original;
        const displayName = formatUserLabel(user);
        return (
          <HorizontalStack gap={3}>
            <RegistryAvatar name={displayName} />
            <VerticalStack gap={0.5}>
              <div className="text-[13px] font-bold text-foreground font-heading truncate">{displayName}</div>
              <div className="text-[11px] text-muted-foreground truncate">{user.email}</div>
            </VerticalStack>
          </HorizontalStack>
        );
      },
      size: 300,
    },
    {
      id: 'roles',
      header: 'Roles',
      cell: ({ row }) => {
        const user = row.original;
        const membership = (user.tenantMemberships ?? []).find(m => m.tenantId === tenantId);
        const roles = membership?.roles ?? [];
        return (
          <div className="flex flex-wrap gap-1">
            {roles.length > 0 ? (
              roles.map(r => (
                <RegistryStatus
                  key={r}
                  label={formatRoleLabel(r)}
                  variant={getRoleVariant(r)}
                />
              ))
            ) : (
              <RegistryStatus label="No role" variant="muted" />
            )}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const user = row.original;
        const isCurrentUser = user.id === currentUserId;
        if (isCurrentUser) {
          return (
            <span className="rounded border border-border/40 px-1 py-0.5 text-[9px] font-black uppercase tracking-widest text-muted-foreground/50">
              You
            </span>
          );
        }
        return (
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:bg-destructive/10 h-8 px-2"
            disabled={actingId === user.id}
            onClick={() => setConfirmState({ type: 'member', id: user.id, label: formatUserLabel(user) })}
          >
            <UserX className="h-3.5 w-3.5 mr-1" />
            Revoke
          </Button>
        );
      },
      size: 100,
    },
  ], [tenantId, currentUserId, actingId]);

  const inviteColumns = useMemo<ColumnDef<TenantMemberInvite>[]>(() => [
    {
      id: 'email',
      header: 'Email',
      cell: ({ row }) => {
        const invite = row.original;
        return (
          <HorizontalStack gap={3}>
            <RegistryAvatar name={invite.email} />
            <div className="text-[13px] font-bold text-foreground font-heading truncate">{invite.email}</div>
          </HorizontalStack>
        );
      },
      size: 300,
    },
    {
      id: 'role',
      header: 'Role',
      cell: ({ row }) => {
        const invite = row.original;
        return (
          <RegistryStatus
            label={formatRoleLabel(invite.roleCode)}
            variant={getRoleVariant(invite.roleCode)}
          />
        );
      },
    },
    {
      id: 'created',
      header: 'Created',
      cell: ({ row }) => {
        const invite = row.original;
        return (
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground font-medium">
            <Clock className="h-3 w-3" />
            {formatRelativeTime(invite.createdAt)}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const invite = row.original;
        return (
          <HorizontalStack gap={1} className="justify-end">
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => void handleCopyInvite(invite)}
            >
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              Copy
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:bg-destructive/10 h-8 px-2"
              disabled={actingId === invite.id}
              onClick={() =>
                setConfirmState({
                  type: 'invite',
                  id: invite.id,
                  label: invite.email,
                })
              }
            >
              Revoke
            </Button>
          </HorizontalStack>
        );
      },
      size: 180,
    },
  ], [actingId, handleCopyInvite]);

  // ── Guard: no tenant ───────────────────────────────────────────────────────
  if (!tenantId) {
    return (
      <ManagementRegistry
        parentLabel="Users"
        activeLabel="Tenant users"
        description="Select an active tenant to manage teammates."
      >
        <Callout variant="info">Select an active tenant to manage teammates.</Callout>
      </ManagementRegistry>
    );
  }

  // ── Guard: no permission ───────────────────────────────────────────────────
  if (!canManage) {
    return (
      <ManagementRegistry
        parentLabel="Users"
        activeLabel="Tenant users"
        description="Tenant user management requires the tenant.members.manage permission."
      >
        <Callout variant="warning">
          Tenant user management requires the{' '}
          <code className="font-mono text-[11px]">tenant.members.manage</code> permission.
        </Callout>
      </ManagementRegistry>
    );
  }

  const isActiveView = section === 'active';

  return (
    <>
      <ManagementRegistry
        parentLabel="Users"
        activeLabel={isActiveView ? 'Active Members' : 'Invite Links'}
        description={
          isActiveView
            ? 'Review current tenant memberships and revoke access when no longer needed.'
            : 'Create and manage email-bound invitations for this tenant.'
        }
        headerActions={
          <HorizontalStack gap={2}>
            <Button size="sm" variant="outline" onClick={() => void loadData()} disabled={loading}>
              <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', loading && 'animate-spin')} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setInviteOpen(true)}>
              <UserPlus className="h-3.5 w-3.5 mr-1.5" />
              Invite Teammate
            </Button>
          </HorizontalStack>
        }
        metrics={[
          { label: 'Members', value: activeMembers.length },
          { label: 'Admins & Owners', value: adminCount },
          { label: 'Pending Invites', value: pendingInvites.length },
        ]}
        toolbar={
          <CondensedToolbar>
            <ToolbarItem label="Search" flex>
              <SilkInput
                placeholder={isActiveView ? "Search members by name or email..." : "Search invites by email..."}
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoComplete="off"
              />
            </ToolbarItem>
          </CondensedToolbar>
        }
        isLoading={loading && (isActiveView ? activeMembers.length === 0 : pendingInvites.length === 0)}
      >
        <DataTable
          columns={isActiveView ? activeColumns : (inviteColumns as ColumnDef<any>[])}
          data={isActiveView ? activeMembers : pendingInvites}
          globalFilter={search}
          onGlobalFilterChange={setSearch}
          layout="document"
          emptyState={
            <RegistryEmptyState
              icon={isActiveView ? Users : Mail}
              title={isActiveView ? "No members yet" : "No pending invites"}
              description={
                isActiveView
                  ? "This tenant has no active members. Invite teammates to get started."
                  : "Create an invite link to onboard a teammate."
              }
            />
          }
          noResultsState={
            <RegistryEmptyState
              icon={isActiveView ? Users : Mail}
              title="No results found"
              description="Try adjusting your search terms."
            />
          }
        />
      </ManagementRegistry>

      {/* ── Invite Sheet ────────────────────────────────────────────────── */}
      <Sheet open={inviteOpen} onOpenChange={setInviteOpen}>
        <SheetContent side="right">
          <SheetHeader>
            <SheetTitle>Invite Teammate</SheetTitle>
            <SheetDescription>
              Create an email-bound invitation link. The link will be copied to your clipboard automatically.
            </SheetDescription>
          </SheetHeader>
          
          <VerticalStack gap={6} className="mt-8">
            <VerticalStack gap={2}>
              <Label htmlFor="invite-email" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                Email Address
              </Label>
              <SilkInput
                id="invite-email"
                type="email"
                placeholder="teammate@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') void handleCreateInvite();
                }}
              />
            </VerticalStack>

            <VerticalStack gap={2}>
              <Label htmlFor="invite-role" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                Tenant Role
              </Label>
              <SilkSelect
                id="invite-role"
                value={roleCode}
                onValueChange={setRoleCode}
                options={ROLE_OPTIONS}
              />
            </VerticalStack>

            <Callout variant="info" className="text-xs">
              Invite links are tied to a specific email address and can only be redeemed once.
            </Callout>

            <Button
              className="w-full mt-4"
              onClick={() => void handleCreateInvite()}
              disabled={creating || !email.trim()}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              {creating ? 'Creating invite…' : 'Create & Copy Invite Link'}
            </Button>
          </VerticalStack>
        </SheetContent>
      </Sheet>

      <RevokeDialog
        state={confirmState}
        acting={actingId !== null}
        onConfirm={() => void handleConfirmRevoke()}
        onCancel={() => setConfirmState(null)}
      />
    </>
  );
}

// ── Revoke Confirmation Dialog ─────────────────────────────────────────────

interface RevokeDialogProps {
  state: ConfirmState | null;
  acting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function RevokeDialog({ state, acting, onConfirm, onCancel }: RevokeDialogProps) {
  const isMember = state?.type === 'member';
  return (
    <Dialog open={state !== null} onOpenChange={open => { if (!open) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isMember ? 'Revoke member access' : 'Revoke invite'}</DialogTitle>
          <DialogDescription>
            {isMember
              ? `Remove ${state?.label ?? 'this user'} from this tenant? They will immediately lose access and must be re-invited to rejoin.`
              : `Cancel the invite sent to ${state?.label ?? 'this address'}? The link will no longer be redeemable.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={acting}>
              Cancel
            </Button>
          </DialogClose>
          <Button variant="destructive" disabled={acting} onClick={onConfirm}>
            {acting ? 'Revoking…' : isMember ? 'Revoke access' : 'Revoke invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
